import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { aiProviders } from '../data/ai-providers';
import { Tenant } from '../types/user';
import { Assistant } from '../types/assistant';

interface DefaultChatScreenProps {
  tenant: Tenant;
  assistants: Assistant[];
  onSelectAI: (aiId: string, modelId: string) => void;
  onAssistantSelect: (assistant: Assistant) => void;
  onSendMessage?: (message: string, aiId: string, modelId: string) => void;
}

export function DefaultChatScreen({ tenant, assistants, onSelectAI, onAssistantSelect, onSendMessage }: DefaultChatScreenProps) {
  const [message, setMessage] = useState('');

  // Filtrar apenas as IAs habilitadas para o tenant
  const availableAIs = aiProviders.filter(ai => tenant.settings?.enabledAIs?.includes(ai.id) ?? true);

  // Mapear IDs para ícones específicos do design
  const aiDisplayConfig = {
    'openai': {
      icon: '🤖',
      displayName: 'GPT',
      subtitle: 'OpenAI GPT',
      bgColor: 'bg-green-50',
      textColor: 'text-green-900',
      borderColor: 'border-green-200'
    },
    'anthropic': {
      icon: '🎭',
      displayName: 'CLAUDE',
      subtitle: 'Anthropic',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-900',
      borderColor: 'border-orange-200'
    },
    'google': {
      icon: '💎',
      displayName: 'GEMINI',
      subtitle: 'Google AI',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-900',
      borderColor: 'border-blue-200'
    },
    'perplexity': {
      icon: '🔍',
      displayName: 'PERPLEXITY',
      subtitle: 'Com busca web',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-900',
      borderColor: 'border-purple-200'
    }
  };

  const handleAISelect = (aiId: string) => {
    const currentAI = aiProviders.find(ai => ai.id === aiId);
    if (currentAI && currentAI.models.length > 0) {
      onSelectAI(aiId, currentAI.models[0].id);
    }
  };

  const handleSendMessage = () => {
    if (message.trim() && onSendMessage) {
      // Usar a primeira IA disponível como padrão
      const defaultAI = availableAIs[0];
      if (defaultAI && defaultAI.models.length > 0) {
        onSelectAI(defaultAI.id, defaultAI.models[0].id);
        onSendMessage(message.trim(), defaultAI.id, defaultAI.models[0].id);
        setMessage('');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };


  return (
    <div className="flex flex-col h-full bg-white">
      {/* Área principal centralizada */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 max-w-5xl mx-auto w-full">
        {/* Título principal */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            SPACE
          </h1>
          <p className="text-lg text-gray-600">
            Conecte-se com as melhores IAs em uma única plataforma
          </p>
        </div>

        {/* Seleção de IA */}
        <div className="w-full max-w-4xl">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">
            Escolha sua IA:
          </h2>

          {/* Grid de IAs - Centralizado */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {availableAIs.map((ai) => {
              const config = aiDisplayConfig[ai.id as keyof typeof aiDisplayConfig];

              return (
                <button
                  key={ai.id}
                  onClick={() => handleAISelect(ai.id)}
                  className="relative p-6 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 hover:border-gray-300 hover:scale-105 transition-all duration-200 w-[140px]"
                >
                  {/* Ícone */}
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold bg-white text-gray-900 shadow-sm">
                      {ai.id === 'openai' ? (
                        <img 
                          src="/gpt-logo.png?v=1" 
                          alt="GPT Logo" 
                          className="w-8 h-8 object-contain" 
                          style={{ maxWidth: '32px', maxHeight: '32px' }}
                        />
                      ) : ai.id === 'anthropic' ? (
                        <img 
                          src="/claude-logo.png?v=1" 
                          alt="Claude Logo" 
                          className="w-8 h-8 object-contain"
                          style={{ maxWidth: '32px', maxHeight: '32px' }}
                        />
                      ) : ai.id === 'google' ? (
                        <img 
                          src="/gemini-logo.png?v=1" 
                          alt="Gemini Logo" 
                          className="w-8 h-8 object-contain"
                          style={{ maxWidth: '32px', maxHeight: '32px' }}
                        />
                      ) : (
                        config?.icon || '🤖'
                      )}
                    </div>
                  </div>

                  {/* Nome da IA */}
                  <div className="text-center">
                    <div className="text-sm font-bold mb-1 text-gray-900">
                      {config.displayName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {config.subtitle}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Seção de Agentes */}
        {assistants.length > 0 && (
          <div className="w-full max-w-4xl mt-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
              Seus Agentes
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {assistants.slice(0, 8).map((assistant) => (
                <button
                  key={assistant.id}
                  onClick={() => onAssistantSelect(assistant)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center hover:shadow-md transition-all hover:scale-105 w-[160px]"
                >
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-2xl flex-shrink-0">
                      {assistant.avatar || '🤖'}
                    </div>
                    <div className="text-center w-full overflow-hidden">
                      <h4 className="font-medium text-gray-900 text-sm truncate">{assistant.name}</h4>
                      {assistant.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">{assistant.description}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {assistants.length > 8 && (
              <div className="text-center mt-4">
                <p className="text-sm text-gray-500">+{assistants.length - 8} agentes no menu lateral</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input inferior */}
      <div className="border-t bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3 items-center">
            <div className="flex-1 relative">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Pergunte qualquer coisa..."
                className="pr-12 h-12 text-base bg-white border-gray-300 focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
              />
            </div>
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim()}
              className="h-12 px-6 text-white rounded-lg transition-colors bg-blue-600 hover:bg-blue-700"
            >
              <span className="text-lg">→</span>
            </Button>
          </div>

          {/* Informações adicionais */}
          <div className="flex justify-center items-center mt-3 text-xs text-gray-500">
            <span>
              {availableAIs.length} IAs disponíveis • 
              Press Enter para enviar
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}